import React, { useState, useEffect } from 'react';
import "./main.css";
import { RequestBody, fetchCars } from './services/api';
import { useCars } from './hooks/useCars';
import Header from './components/Header';
import FilterSidebar from './components/FilterSidebar';
import CarList from './components/CarList';
import { exportToCSV, exportToJSON } from './utils/exportUtils';

function useWindowSize() {
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        function handleResize() {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        }

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return windowSize;
}

function App() {
    // Load filters from local storage on initial render
    const [requestBody, setRequestBody] = useState<RequestBody>(() => {
        const savedFilters = localStorage.getItem('carFilters');
        if (savedFilters) {
            try {
                return JSON.parse(savedFilters);
            } catch (error) {
                console.error('Failed to parse saved filters:', error);
            }
        }
        return {
            makes: null,
            model: null,
            body: null,
            drivetrain: null,
            yearlt: null,
            yeargt: null,
            collisionlt: null,
            collisiongt: null,
            complt: null,
            compgt: null,
            dcpdlt: null,
            dcpdgt: null,
        };
    });

    const [page, setPage] = useState(1);
    const limit = 200;
    const { data, isLoading, isError } = useCars(requestBody, page, limit);
    const cars = data?.cars || [];
    const total = data?.total || 0;
    const totalPages = Math.ceil(total / limit);

    const size = useWindowSize();
    const isMobile = size.width < 768;
    const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 768);

    // Theme Logic
    const [theme, setTheme] = useState<'light' | 'dark'>(() => {
        const saved = localStorage.getItem('theme');
        if (saved === 'light' || saved === 'dark') return saved;
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });

    useEffect(() => {
        document.body.dataset.theme = theme;
    }, [theme]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
            if (!localStorage.getItem('theme')) {
                setTheme(e.matches ? 'dark' : 'light');
            }
        };
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const toggleTheme = () => {
        setTheme(prev => {
            const newTheme = prev === 'light' ? 'dark' : 'light';
            localStorage.setItem('theme', newTheme);
            return newTheme;
        });
    };

    // Save filters to local storage whenever they change
    useEffect(() => {
        localStorage.setItem('carFilters', JSON.stringify(requestBody));
    }, [requestBody]);

    // Reset page when filters change
    useEffect(() => {
        setPage(1);
    }, [requestBody]);

    // Handler to export all filtered data
    const handleExportAll = async (format: 'csv' | 'json') => {
        try {
            // Fetch all data without pagination
            const allData = await fetchCars(requestBody, 1, total);
            const timestamp = new Date().toISOString().split('T')[0];
            const filename = `car-insurance-all-${timestamp}.${format}`;

            if (format === 'csv') {
                exportToCSV(allData.cars, filename);
            } else {
                exportToJSON(allData.cars, filename);
            }
        } catch (error) {
            console.error('Failed to export all data:', error);
            alert('Failed to export data. Please try again.');
        }
    };

    return (
        <div className="app-container">
            <Header
                theme={theme}
                toggleTheme={toggleTheme}
                onToggleSidebar={() => setIsSidebarOpen(prev => !prev)}
                currentPageData={cars}
                totalCount={total}
                requestBody={requestBody}
                onExportAll={handleExportAll}
            />

            <FilterSidebar
                isOpen={isSidebarOpen}
                setIsOpen={setIsSidebarOpen}
                requestBody={requestBody}
                setRequestBody={setRequestBody}
            />

            <main className="main-content">
                <CarList
                    cars={cars}
                    isLoading={isLoading}
                    isError={isError}
                    page={page}
                    setPage={setPage}
                    totalPages={totalPages}
                    isMobile={isMobile}
                />
            </main>
        </div>
    );
}

export default App;
